import { useEffect } from 'react'
import { SITE_ORIGIN, OG_IMAGE_URL } from '../data/dgpt'

type PageMeta = {
  title: string
  description: string
  /** Path beginning with "/", e.g. "/tools/market-research". */
  path: string
  keywords?: string[]
  /** Page-specific JSON-LD nodes (site-wide Organization / WebSite are added separately). */
  jsonLd?: Record<string, unknown>[]
}

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

function removeMeta(attribute: 'name' | 'property', key: string) {
  document.head.querySelector(`meta[${attribute}="${key}"]`)?.remove()
}

export function canonicalUrl(path: string) {
  return path === '/' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${path}`
}

/** Sets unique title, description, canonical, Open Graph / Twitter tags and page JSON-LD. */
export function usePageMeta({ title, description, path, keywords, jsonLd }: PageMeta) {
  const keywordString = keywords?.join(', ') ?? ''
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : ''

  useEffect(() => {
    const url = canonicalUrl(path)
    document.title = title
    upsertMeta('name', 'description', description)
    if (keywordString) upsertMeta('name', 'keywords', keywordString)
    else removeMeta('name', 'keywords')

    upsertMeta('property', 'og:site_name', 'DiligenceGPT™')
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:type', 'website')
    upsertMeta('property', 'og:url', url)
    upsertMeta('name', 'twitter:title', title)
    upsertMeta('name', 'twitter:description', description)
    if (OG_IMAGE_URL) {
      upsertMeta('property', 'og:image', OG_IMAGE_URL)
      upsertMeta('name', 'twitter:card', 'summary_large_image')
    } else {
      removeMeta('property', 'og:image')
      upsertMeta('name', 'twitter:card', 'summary')
    }

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = url

    let script = document.head.querySelector<HTMLScriptElement>('script[data-page-jsonld]')
    if (jsonLdString) {
      if (!script) {
        script = document.createElement('script')
        script.type = 'application/ld+json'
        script.setAttribute('data-page-jsonld', 'true')
        document.head.appendChild(script)
      }
      script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': JSON.parse(jsonLdString) })
    } else {
      script?.remove()
    }
  }, [title, description, path, keywordString, jsonLdString])
}
